import { Fragment, ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, Settings2Icon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import Select from 'app/frontend/base/components/Select'
import { characterOption } from 'app/frontend/base/components/ApplicationCharacterCard'
import { AbilitySpell } from 'app/frontend/types/custom/Abilities'
import { RaidPlanActor, RaidPlanCast } from 'app/frontend/types/custom/RaidPlan'
import ActorTimelineContainer, {
  ActorTimelineRow,
  sortActorTimelineRows,
} from './ActorTimelineContainer'
import ActorAbilityContainer from './ActorAbilityContainer'
import ActorHeader from './ActorHeader'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import SpellIcon from './SpellIcon'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { CharacterClassIcon } from 'app/frontend/base/components'
import { CharacterClass } from 'app/frontend/types/custom'
import CharacterRole from 'app/frontend/types/custom/CharacterRole'
import { cn } from '@/lib/utils'
import { NOTE_ABILITY_ID, NOTE_ICON_NAME } from '../config/generalAbilities'
import mapGeneralCastsWithOverlap from './generalCastOverlap'
import { getTeamPlanActorSortMeta } from './teamPlanActorSorting'
import ActorSpecializationMenu from './ActorSpecializationMenu'
import {
  getGeneralTimelineAbilities,
  getRaidPlanAbilities,
} from 'app/frontend/base/services/raidPlanData'
import ImportActorFromWarcraftLogsDialog from './ImportActorFromWarcraftLogsDialog'

type TeamPlanActorRowsProps = {
  hoverTime: number | null
  onTimelineMouseMove: (event: React.MouseEvent<HTMLDivElement>) => void
  onTimelineMouseLeave: () => void
  onTimelineHoverTimeChange: (nextHoverTime: number) => void
  onActorRowHoverMove: (
    clientX: number,
    timelineLeft: number,
    timelineWidth: number,
  ) => void
  inFocusedDialog?: boolean
  beforeActorContentByUuid?: Record<string, ReactNode>
  actorRowBehaviorByUuid?: Record<
    string,
    {
      showPreferredCharacter?: boolean
      allowRemove?: boolean
      allowSpecializationEdit?: boolean
      allowCastEdit?: boolean
    }
  >
}

const TeamPlanActorRows = ({
  hoverTime,
  onTimelineMouseMove,
  onTimelineMouseLeave,
  onTimelineHoverTimeChange,
  onActorRowHoverMove,
  inFocusedDialog = false,
  beforeActorContentByUuid,
  actorRowBehaviorByUuid,
}: TeamPlanActorRowsProps) => {
  const { t } = useTranslation()
  const {
    canEdit,
    owner,
    raidPlan,
    rosterMemberships,
    timeline,
    selectedType,
    computed,
    updateTimeline,
  } = useTeamPlanTimeline()
  const {
    specializations,
    castInfoByActor,
    classColor,
    getActorSpells,
    generalAbilities,
  } = computed
  const [hoveredRowKey, setHoveredRowKey] = useState<string | null>(null)
  const [hoveredActorIndex, setHoveredActorIndex] = useState<number | null>(
    null,
  )
  const [openActorIndex, setOpenActorIndex] = useState<number | null>(null)
  const [importActorIndex, setImportActorIndex] = useState<number | null>(null)

  const removeActor = useCallback(
    (actorIndex: number) => {
      updateTimeline((next) => {
        next.actors.splice(actorIndex, 1)
      }, true)
    },
    [updateTimeline],
  )
  const clearActorCasts = useCallback(
    (actorIndex: number) => {
      updateTimeline((next) => {
        next.actors[actorIndex].casts = []
      }, true)
    },
    [updateTimeline],
  )

  const getActorRows = useCallback(
    (
      actor: RaidPlanActor,
      spells: AbilitySpell[],
      castInfoById: Record<
        string,
        {
          duration: number
          cooldown: number
          issue?:
            | { type: 'warning_overlap'; message: string }
            | { type: 'hard_overlap'; message: string }
        }
      >,
    ): ActorTimelineRow[] => {
      const actorColor = classColor(actor.class_id)
      return spells.map((spell) => {
        const casts = actor.casts.filter((cast) => cast.spell_id === spell.id)
        return {
          key: `${actor.label}-${spell.id}`,
          spellId: spell.id,
          casts: casts.map((cast: RaidPlanCast) => {
            const castInfo = castInfoById[cast.id]
            return {
              id: cast.id,
              spellId: cast.spell_id,
              at: cast.at,
              duration: castInfo?.duration ?? 0,
              cooldown: castInfo?.cooldown ?? 0,
              color: actorColor,
              issueType:
                castInfo?.issue?.type === 'hard_overlap'
                  ? 'error'
                  : castInfo?.issue?.type === 'warning_overlap'
                  ? 'warning'
                  : 'none',
              issueMessage: castInfo?.issue?.message,
              message: cast.message,
              ttsMessage: cast.tts_message,
              rgb: cast.rgb,
              targets: cast.targets,
              glowTargetsOnRaidFrames: cast.glow_targets_on_raid_frames,
              addTargetsToReminderText: cast.add_targets_to_reminder_text,
            }
          }),
        }
      })
    },
    [classColor],
  )

  const getActorGeneralRows = useCallback(
    (actor: RaidPlanActor): ActorTimelineRow[] => {
      const actorColor = classColor(actor.class_id)
      return generalAbilities.map((ability) => {
        const isNoteRow = ability.id === NOTE_ABILITY_ID
        const casts = actor.casts.filter((cast) =>
          isNoteRow ? cast.spell_id === null : cast.spell_id === ability.id,
        )
        return {
          key: `${actor.label}-${ability.id}`,
          spellId: isNoteRow ? null : ability.id,
          casts: mapGeneralCastsWithOverlap(
            casts,
            ability.cooldown ?? 0,
            actorColor,
          ),
        }
      })
    },
    [classColor, generalAbilities],
  )

  const specializationsByClassId = useMemo(() => {
    return specializations.reduce<
      Record<
        number,
        {
          id: number
          name: string
          classId: number
          className: string
          icon: string
          defaultRole: CharacterRole
        }[]
      >
    >((acc, specialization) => {
      if (!acc[specialization.classId]) acc[specialization.classId] = []
      acc[specialization.classId].push(specialization)
      return acc
    }, {})
  }, [specializations])

  const raidPlanAbilities = useMemo(() => getRaidPlanAbilities(), [])
  const generalAbilityIds = useMemo(
    () => new Set(getGeneralTimelineAbilities().map((ability) => ability.id)),
    [],
  )
  const generalAbilityById = useMemo(
    () =>
      generalAbilities.reduce<Record<number, typeof generalAbilities[number]>>(
        (acc, ability) => {
          acc[ability.id] = ability
          return acc
        },
        {},
      ),
    [generalAbilities],
  )

  const updateActorSpecialization = useCallback(
    (
      actorIndex: number,
      nextSpecId: number | null,
      nextClassId?: number,
      nextClassName?: string,
    ) => {
      if (!canEdit) return
      updateTimeline((next) => {
        const actor = next.actors[actorIndex]
        const resolvedClassId = nextClassId ?? actor.class_id
        const resolvedClassName = nextClassName ?? actor.class_id.toString()
        actor.class_id = resolvedClassId
        actor.spec_id = nextSpecId
        const classSpellIds = new Set(
          (raidPlanAbilities.classSpells[resolvedClassId.toString()] ?? []).map(
            (spell) => spell.id,
          ),
        )
        const specSpellIds = new Set(
          nextSpecId
            ? (raidPlanAbilities.specSpells[nextSpecId.toString()] ?? []).map(
                (spell) => spell.id,
              )
            : [],
        )
        actor.casts = actor.casts.filter(
          (cast) =>
            cast.spell_id === null ||
            generalAbilityIds.has(cast.spell_id) ||
            classSpellIds.has(cast.spell_id) ||
            specSpellIds.has(cast.spell_id),
        )
        const classSpecCount = next.actors.filter(
          (entry, index) =>
            index !== actorIndex &&
            entry.class_id === actor.class_id &&
            entry.spec_id === actor.spec_id,
        ).length
        actor.label = nextSpecId
          ? `${
              specializations.find((entry) => entry.id === nextSpecId)?.name ??
              resolvedClassName
            } ${classSpecCount + 1}`
          : `${resolvedClassName} ${classSpecCount + 1}`
      }, true)
    },
    [
      canEdit,
      generalAbilityIds,
      raidPlanAbilities,
      specializations,
      updateTimeline,
    ],
  )

  const sortedActors = useMemo(() => {
    const isGeneralTab = selectedType === 'general'
    const actors = timeline.actors.map((actor, actorIndex) => {
      const spells = getActorSpells(actor)
      const { className, spec, classLabel, roleIndex, sortKey } =
        getTeamPlanActorSortMeta(actor, specializations, t('shared.unknown'))
      const hasAbilitiesInActiveSection = isGeneralTab || spells.length > 0
      return {
        actor,
        actorIndex,
        spells,
        className,
        spec,
        classLabel,
        roleIndex,
        sortKey,
        hasAbilitiesInActiveSection,
      }
    })
    if (inFocusedDialog) {
      return actors.map((entry) => ({
        ...entry,
        classSpecOrdinal: 1,
        classSpecTotal: 1,
      }))
    }
    const sorted = actors.sort((a, b) => {
      if (a.hasAbilitiesInActiveSection !== b.hasAbilitiesInActiveSection) {
        return a.hasAbilitiesInActiveSection ? -1 : 1
      }
      if (a.roleIndex !== b.roleIndex) return a.roleIndex - b.roleIndex
      return a.sortKey.localeCompare(b.sortKey)
    })
    const classSpecTotals = actors.reduce<Record<string, number>>(
      (acc, entry) => {
        const classSpecKey = `${entry.actor.class_id}:${
          entry.actor.spec_id ?? 'none'
        }`
        acc[classSpecKey] = (acc[classSpecKey] ?? 0) + 1
        return acc
      },
      {},
    )
    const classSpecCounts: Record<string, number> = {}
    return sorted.map((entry) => {
      const classSpecKey = `${entry.actor.class_id}:${
        entry.actor.spec_id ?? 'none'
      }`
      const nextCount = (classSpecCounts[classSpecKey] ?? 0) + 1
      classSpecCounts[classSpecKey] = nextCount
      return {
        ...entry,
        classSpecOrdinal: nextCount,
        classSpecTotal: classSpecTotals[classSpecKey] ?? 1,
      }
    })
  }, [
    getActorSpells,
    inFocusedDialog,
    selectedType,
    specializations,
    t,
    timeline.actors,
  ])

  const classCharactersByClassId = useMemo(() => {
    const byClassId: Record<
      number,
      { character: wowaudit.CharacterReference; role: CharacterRole }[]
    > = {}
    rosterMemberships.forEach((membership) => {
      const character = membership.characterReference
      if (character.classId === undefined) return
      if (!byClassId[character.classId]) byClassId[character.classId] = []
      byClassId[character.classId].push({
        character,
        role: membership.role,
      })
    })
    return byClassId
  }, [rosterMemberships])

  const preferredCharacterOptionsByClassId = useMemo(() => {
    return Object.entries(classCharactersByClassId).reduce<
      Record<
        number,
        {
          label: ReactNode
          customOptionFn: (isSelected: boolean) => React.ReactNode
          value: string
        }[]
      >
    >((acc, [classIdRaw, characters]) => {
      const classId = Number(classIdRaw)
      acc[classId] = characters.map(({ character, role }) => ({
        label: (
          <span>
            <span className='text-muted-foreground'>Preference: </span>
            <span style={{ color: classColor(classId) }}>{character.name}</span>
          </span>
        ),
        customOptionFn: (isSelected: boolean) =>
          characterOption(character, isSelected, 'role', role),
        value: character.id.toString(),
      }))
      return acc
    }, {})
  }, [classCharactersByClassId, classColor])

  return (
    <>
      {sortedActors.map(
        ({
          actor,
          actorIndex,
          className,
          spec,
          classLabel,
          spells,
          classSpecOrdinal,
          classSpecTotal,
        }) => {
          const castInfoById = castInfoByActor[actorIndex] ?? {}
          const isGeneralTab = selectedType === 'general'
          const actorRowBehavior = actorRowBehaviorByUuid?.[actor.uuid]
          const allowSpecializationEdit =
            actorRowBehavior?.allowSpecializationEdit !== undefined
              ? actorRowBehavior.allowSpecializationEdit
              : canEdit
          const showPreferredCharacter =
            actorRowBehavior?.showPreferredCharacter !== undefined
              ? actorRowBehavior.showPreferredCharacter
              : owner.ownerType === 'team'
          const allowRemove =
            actorRowBehavior?.allowRemove !== undefined
              ? actorRowBehavior.allowRemove
              : canEdit
          const allowCastEdit =
            actorRowBehavior?.allowCastEdit !== undefined
              ? actorRowBehavior.allowCastEdit
              : canEdit
          const beforeActorContent = beforeActorContentByUuid?.[actor.uuid]
          const preferredCharacterOptions =
            preferredCharacterOptionsByClassId[actor.class_id] ?? []
          const preferredCharacterIdValue = preferredCharacterOptions.some(
            (option) =>
              option.value === actor.preferred_character_id?.toString(),
          )
            ? actor.preferred_character_id?.toString()
            : undefined
          const actorRows = isGeneralTab
            ? getActorGeneralRows(actor)
            : getActorRows(actor, spells, castInfoById)
          const sortedActorRows = sortActorTimelineRows(
            actorRows,
            (spellId) => {
              if (spellId === null) {
                return (
                  generalAbilities.find(
                    (ability) => ability.id === NOTE_ABILITY_ID,
                  )?.name ?? 'Note'
                )
              }
              if (isGeneralTab) {
                return generalAbilityById[spellId]?.name ?? `Spell ${spellId}`
              }
              return (
                spells.find((spell) => spell.id === spellId)?.name ??
                `Spell ${spellId}`
              )
            },
            (spellId) => {
              if (spellId === null) return 0
              if (isGeneralTab) {
                return generalAbilityById[spellId]?.cooldown ?? 0
              }
              return spells.find((spell) => spell.id === spellId)?.cooldown ?? 0
            },
          )
          const actorLabelColor = classColor(actor.class_id)
          const actorSubtitleBase = spec?.name
            ? `${spec.name} ${classLabel}`
            : classLabel
          const actorSubtitle =
            classSpecTotal > 1
              ? `${actorSubtitleBase} ${classSpecOrdinal}`
              : actorSubtitleBase
          const specializationGroup = {
            classId: actor.class_id,
            className: className ?? classLabel,
            specializations: specializationsByClassId[actor.class_id] ?? [],
          }
          const labels = sortedActorRows.length ? (
            sortedActorRows.map((row) => {
              if (row.spellId === null) {
                const noteName =
                  generalAbilities.find(
                    (ability) => ability.id === NOTE_ABILITY_ID,
                  )?.name ?? 'Note'
                return (
                  <div
                    key={`${actorIndex}-note`}
                    className='flex items-center gap-2 rounded-sm transition-colors'
                  >
                    <div className='w-40 max-w-40 shrink-0 h-5 text-xs text-foreground truncate flex items-center gap-2'>
                      <span className='truncate grow text-right'>
                        {noteName}
                      </span>
                      <BlizzardIcon
                        iconName={NOTE_ICON_NAME}
                        size='small'
                        className='w-5 h-5 rounded border border-border'
                      />
                    </div>
                  </div>
                )
              }
              const spellName = isGeneralTab
                ? generalAbilityById[row.spellId]?.name ??
                  `Spell ${row.spellId}`
                : spells.find((spell) => spell.id === row.spellId)?.name ??
                  `Spell ${row.spellId}`
              return (
                <div
                  key={`${actorIndex}-${row.spellId}`}
                  className='flex items-center gap-2 rounded-sm transition-colors'
                >
                  <a
                    href={`https://wowhead.com/spell=${row.spellId}`}
                    target='_blank'
                    rel='noreferrer'
                    className={cn(
                      'w-40 max-w-40 shrink-0 h-5 text-xs text-foreground truncate flex items-center gap-2',
                      hoveredRowKey === `${actorIndex}-${row.spellId}`
                        ? 'text-accent-foreground'
                        : '',
                    )}
                  >
                    <span className='truncate grow text-right'>
                      {spellName}
                    </span>
                    <SpellIcon
                      spellId={row.spellId}
                      className='w-5 h-5 rounded border border-border'
                    />
                  </a>
                </div>
              )
            })
          ) : (
            <div className='w-40 max-w-40 shrink-0'>
              <Separator />
            </div>
          )
          const timelineContent =
            isGeneralTab || spells.length ? (
              <ActorTimelineContainer
                storageTarget={{ type: 'actor', actorIndex }}
                rows={sortedActorRows}
                canEditCasts={allowCastEdit}
                hoverTime={hoverTime}
                onHoverTimeChange={onTimelineHoverTimeChange}
                onHoveredRowSpellIdChange={(spellId) =>
                  setHoveredRowKey(
                    spellId === null ? null : `${actorIndex}-${spellId}`,
                  )
                }
                className={inFocusedDialog ? 'text-card' : 'text-card-nested'}
              />
            ) : (
              <Separator />
            )

          return (
            <Fragment key={`${actor.spec_id}-${actorIndex}`}>
              {beforeActorContent}
              <div
                className='relative z-10 group'
                onMouseMove={(event) => {
                  const timelineElement = event.currentTarget.querySelector(
                    '[data-actor-row-timeline="true"]',
                  )
                  if (!(timelineElement instanceof HTMLDivElement)) return
                  const rect = timelineElement.getBoundingClientRect()
                  onActorRowHoverMove(event.clientX, rect.left, rect.width)
                }}
                onMouseLeave={onTimelineMouseLeave}
              >
                <ActorHeader
                  title={
                    allowSpecializationEdit ? (
                      <ActorSpecializationMenu
                        group={specializationGroup}
                        align='start'
                        onOpenChange={(open) =>
                          setOpenActorIndex(open ? actorIndex : null)
                        }
                        onSelectClass={(classId, selectedClassName) => {
                          setOpenActorIndex(null)
                          updateActorSpecialization(
                            actorIndex,
                            null,
                            classId,
                            selectedClassName,
                          )
                        }}
                        onSelectSpecialization={(selectedSpec) => {
                          setOpenActorIndex(null)
                          updateActorSpecialization(
                            actorIndex,
                            selectedSpec.id,
                            selectedSpec.classId,
                            selectedSpec.className,
                          )
                        }}
                        trigger={
                          <button
                            type='button'
                            className='flex items-center gap-1.5 transition-colors min-w-0 pr-4 pt-1 cursor-pointer'
                            style={{ color: actorLabelColor }}
                            onMouseEnter={() =>
                              setHoveredActorIndex(actorIndex)
                            }
                            onMouseLeave={() => setHoveredActorIndex(null)}
                          >
                            {hoveredActorIndex === actorIndex ||
                            openActorIndex === actorIndex ? (
                              <div className='w-4 flex items-center justify-center'>
                                <Pencil className='h-3 w-3 shrink-0 text-foreground' />
                              </div>
                            ) : spec?.icon ? (
                              <BlizzardIcon
                                iconName={spec?.icon}
                                size='small'
                              />
                            ) : (
                              <CharacterClassIcon
                                characterClass={className as CharacterClass}
                                size='small'
                              />
                            )}
                            <span className='truncate'>{actorSubtitle}</span>
                          </button>
                        }
                      />
                    ) : (
                      <div
                        className='flex items-center gap-1.5 transition-colors min-w-0 pr-4 pt-1'
                        style={{ color: actorLabelColor }}
                      >
                        {spec?.icon ? (
                          <BlizzardIcon iconName={spec?.icon} size='small' />
                        ) : (
                          <CharacterClassIcon
                            characterClass={className as CharacterClass}
                            size='small'
                          />
                        )}
                        <span className='truncate'>{actorSubtitle}</span>
                      </div>
                    )
                  }
                  action={
                    <div className='flex items-center gap-1.5'>
                      {showPreferredCharacter && canEdit ? (
                        <Select
                          options={preferredCharacterOptions}
                          value={preferredCharacterIdValue}
                          setValue={(value) => {
                            if (!canEdit) return
                            updateTimeline((next) => {
                              next.actors[actorIndex].preferred_character_id =
                                value ? Number(value) : null
                            }, true)
                          }}
                          placeholder='no preferred character'
                          className='w-fit font-normal text-xs h-4 hover:bg-accent [&>svg]:h-5 [&>svg]:w-5 px-2'
                          isClearable
                          hideIcon
                          optionWidth='fit'
                        />
                      ) : showPreferredCharacter ? (
                        <span className='text-xs text-muted-foreground'>
                          {
                            preferredCharacterOptions.find(
                              (option) =>
                                option.value ===
                                actor.preferred_character_id?.toString(),
                            )?.label
                          }
                        </span>
                      ) : null}
                      {allowRemove ? (
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size='icon'
                              variant='outline'
                              className='h-4.5 w-4.5 shrink-0'
                            >
                              <Settings2Icon className='h-3 w-3' />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align='end'
                            className={
                              inFocusedDialog
                                ? 'pointer-events-auto'
                                : undefined
                            }
                            onFocusOutside={
                              inFocusedDialog
                                ? (event) => event.preventDefault()
                                : undefined
                            }
                          >
                            <DropdownMenuItem
                              onClick={() => setImportActorIndex(actorIndex)}
                              className={
                                inFocusedDialog
                                  ? 'hover:bg-accent hover:text-accent-foreground'
                                  : undefined
                              }
                            >
                              Import from Warcraft Logs
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => clearActorCasts(actorIndex)}
                              className={
                                inFocusedDialog
                                  ? 'hover:bg-accent hover:text-accent-foreground'
                                  : undefined
                              }
                            >
                              Clear all casts
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => removeActor(actorIndex)}
                              className={cn(
                                'text-destructive-foreground focus:text-destructive-foreground',
                                inFocusedDialog &&
                                  'hover:bg-accent hover:text-destructive-foreground',
                              )}
                            >
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                    </div>
                  }
                />
                <div className='flex items-start gap-1'>
                  <ActorAbilityContainer
                    labels={labels}
                    labelsInCard={isGeneralTab || spells.length > 0}
                  />
                  <div
                    data-actor-row-timeline='true'
                    className='grow min-w-0'
                    onMouseMove={onTimelineMouseMove}
                    onMouseLeave={onTimelineMouseLeave}
                  >
                    {timelineContent}
                  </div>
                </div>
              </div>
            </Fragment>
          )
        },
      )}
      <ImportActorFromWarcraftLogsDialog
        open={importActorIndex !== null}
        onOpenChange={(open) => {
          if (!open) setImportActorIndex(null)
        }}
        owner={owner}
        encounterId={raidPlan.encounterId}
        classId={
          importActorIndex !== null
            ? timeline.actors[importActorIndex]?.class_id ?? 0
            : 0
        }
        specId={
          importActorIndex !== null
            ? timeline.actors[importActorIndex]?.spec_id ?? null
            : null
        }
        onImportCasts={(casts) => {
          if (importActorIndex === null) return
          updateTimeline((next) => {
            if (!next.actors[importActorIndex]) return
            next.actors[importActorIndex].casts = casts
          }, true)
        }}
      />
    </>
  )
}

export default TeamPlanActorRows
